import React, { ErrorInfo } from 'react';
import sendUIErrorNotification from '../../services/uiErrorNotifier';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidMount() {
    window.addEventListener('error', ErrorBoundary.handleGlobalError);
    window.addEventListener('unhandledrejection', ErrorBoundary.handleUnhandledRejection);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    sendUIErrorNotification({
      errorMessage: error.message,
      componentStack: errorInfo.componentStack || undefined,
      page: window.location.pathname,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('error', ErrorBoundary.handleGlobalError);
    window.removeEventListener('unhandledrejection', ErrorBoundary.handleUnhandledRejection);
  }

  static handleGlobalError(event: ErrorEvent) {
    sendUIErrorNotification({
      errorMessage: event.message || 'Uncaught error',
      componentStack: `${event.filename}:${event.lineno}:${event.colno}`,
      page: window.location.pathname,
    });
  }

  static handleUnhandledRejection(event: PromiseRejectionEvent) {
    const message = event.reason?.message || event.reason || 'Unhandled promise rejection';
    sendUIErrorNotification({
      errorMessage: String(message),
      page: window.location.pathname,
    });
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>The application encountered an unexpected error. Please refresh the page or contact support.</p>
          <button type="button" onClick={() => window.location.reload()} style={{ marginTop: '16px', padding: '8px 24px', cursor: 'pointer' }}>
            Refresh Page
          </button>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
